import React from 'react';
import { SupportMessageBlock } from 'types/shared/documents/support';
import { ticketStatusLabel } from './helpers';

type Props = {
  // A realtime snapshot can briefly deliver a message without `content`, so
  // this is optional and defaulted — never map over undefined.
  blocks?: SupportMessageBlock[];
  /** When provided, multiple-choice options are clickable and post the choice. */
  onSelectOption?: (value: string) => void;
};

// Attachment URLs are client-supplied Firestore data; only http(s) may become
// href/src (a javascript: URL would run in the dashboard session).
const isSafeUrl = (url: string) => {
  try {
    const protocol = new URL(url).protocol;
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

const MessageBlocks = ({ blocks, onSelectOption }: Props) => (
  <>
    {(blocks || []).map((block, index) => {
      switch (block.type) {
        case 'text':
          return (
            <p key={index} className="support-message__text">
              {block.text}
            </p>
          );
        case 'multiple_choice':
          return (
            <div key={index} className="support-message__choices">
              {block.prompt && (
                <p className="support-message__text">{block.prompt}</p>
              )}
              {block.options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="support-chip"
                  disabled={!onSelectOption || !!block.selectedId}
                  onClick={() => onSelectOption?.(option.value ?? option.label)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          );
        case 'article_card':
          return (
            <div key={index} className="support-card">
              📄 {block.title || block.articleId}
            </div>
          );
        case 'ticket_card':
          return (
            <div key={index} className="support-card">
              🎫 {block.title || block.ticketId}
              {block.status ? ` · ${ticketStatusLabel(block.status)}` : ''}
            </div>
          );
        case 'escalation':
          return (
            <div key={index} className="support-card">
              ↗ An Team „{block.team}“ weitergeleitet
              {block.note ? `: ${block.note}` : ''}
            </div>
          );
        case 'attachment':
          if (!isSafeUrl(block.url)) {
            return (
              <span key={index} className="support-card">
                📎 {block.name}
              </span>
            );
          }
          if (block.mimeType?.startsWith('image/')) {
            return (
              <a
                key={index}
                className="support-attachment"
                href={block.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="support-attachment__media"
                  src={block.url}
                  alt={block.name}
                  loading="lazy"
                />
              </a>
            );
          }
          if (block.mimeType?.startsWith('video/')) {
            return (
              <video
                key={index}
                className="support-attachment__media"
                src={block.url}
                controls
                preload="metadata"
              />
            );
          }
          return (
            <a
              key={index}
              className="support-card"
              href={block.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              📎 {block.name}
            </a>
          );
        case 'function_call':
        default:
          return null;
      }
    })}
  </>
);

export default MessageBlocks;
